/**
 * Rotas da aplicação.
 *
 * Duas zonas com públicos e regras diferentes:
 *
 * - O menu do cliente, sem autenticação, aberto pelo QR da mesa. Usa a
 *   identidade visual da gelataria e os mesmos tokens da aplicação.
 * - O atendimento e a gestão, atrás de sessão, com navegação por perfil.
 *
 * O `RotaProtegida` é conveniência, não segurança. Quem forçar um endereço
 * leva 403 do servidor, que é quem decide.
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { SessionProvider, useSessao } from './auth/SessionContext';
import { AppShell } from './components/shell/AppShell';
import Loader from './components/common/Loader';
import PageLoadBoundary from './components/common/PageLoadBoundary';
import LoginPage from './pages/Login';
import type { TenantRole } from './types/domain';

import './styles/tokens.css';

// Tudo em chunks separados. O menu do cliente também: além de o staff não
// precisar de o descarregar, o CSS do template que o veste pinta `body`,
// `section` e `header` a escuro, e carregado sempre pintava também os ecrãs
// internos.
const MenuBamidi = lazy(() => import('./pages/MenuBamidi'));
const StaffWorkspace = lazy(() => import('./pages/StaffWorkspace'));
const ManagerDashboard = lazy(() => import('./pages/ManagerDashboard'));
const HistoricoPage = lazy(() => import('./pages/manage/HistoricoPage'));
const MenuAdmin = lazy(() => import('./pages/manage/MenuAdmin'));
const MesasAdmin = lazy(() => import('./pages/manage/MesasAdmin'));
const ContasAdmin = lazy(() => import('./pages/manage/ContasAdmin'));

const GESTAO: TenantRole[] = ['owner', 'manager', 'superuser'];
const SALA: TenantRole[] = ['owner', 'manager', 'waiter', 'superuser'];

const LegacyStaffRedirect = () => {
  const location = useLocation();
  return <Navigate to={'/staff-mobile' + location.search + location.hash} replace />;
};

const RotaProtegida = ({
  children,
  papeis,
}: {
  children: React.ReactNode;
  papeis?: TenantRole[];
}) => {
  const { sessao } = useSessao();
  const location = useLocation();

  if (!sessao) return <Navigate to="/login" state={{ from: location.pathname + location.search + location.hash }} replace />;

  const papel = sessao.tenants[0]?.role;
  if (papeis && (!papel || !papeis.includes(papel))) {
    return (
      <AppShell>
        <main className="workspace">
          <div className="empty">
            <strong>Sem permissão</strong>
            <span>{papel === 'kitchen' ? 'A área de cozinha foi descontinuada. Pede ao gerente para atualizar o teu perfil de acesso.' : 'O teu perfil não tem acesso a esta área.'}</span>
          </div>
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Suspense fallback={<Loader />}>{children}</Suspense>
    </AppShell>
  );
};

const Rotas = () => (
  <Routes>
    {/* Cliente — sem sessão, identificado pelo token do QR. */}
    <Route
      path="/"
      element={
        <Suspense fallback={<Loader />}>
          <MenuBamidi />
        </Suspense>
      }
    />
    <Route path="/login" element={<LoginPage />} />

    {/* Endereços antigos mantêm o acesso ao atendimento. */}
    <Route
      path="/cozinha"
      element={<LegacyStaffRedirect />}
    />
    <Route
      path="/waiter"
      element={<LegacyStaffRedirect />}
    />
    <Route
      path="/staff-mobile"
      element={
        <RotaProtegida papeis={SALA}>
          <StaffWorkspace />
        </RotaProtegida>
      }
    />

    {/* Gestão — apenas gerentes e proprietários. */}
    <Route
      path="/manager"
      element={
        <RotaProtegida papeis={GESTAO}>
          <ManagerDashboard />
        </RotaProtegida>
      }
    />
    <Route
      path="/historico"
      element={
        <RotaProtegida papeis={GESTAO}>
          <HistoricoPage />
        </RotaProtegida>
      }
    />
    <Route
      path="/admin/menu"
      element={
        <RotaProtegida papeis={GESTAO}>
          <MenuAdmin />
        </RotaProtegida>
      }
    />
    <Route
      path="/admin/mesas"
      element={
        <RotaProtegida papeis={GESTAO}>
          <MesasAdmin />
        </RotaProtegida>
      }
    />
    <Route
      path="/admin/contas"
      element={
        <RotaProtegida papeis={GESTAO}>
          <ContasAdmin />
        </RotaProtegida>
      }
    />

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const App = () => (
  <SessionProvider>
    <BrowserRouter>
      <PageLoadBoundary><Rotas /></PageLoadBoundary>
    </BrowserRouter>
  </SessionProvider>
);

export default App;
